import React, { useState, useEffect, useCallback } from 'react';

interface TradeCoinRecord {
  id: number;
  trade_type: string;
  code: string;
  ord_type: string;
  side: string;
  price: number | null;
  volume: number | null;
  order_uuid: string;
  state: string;
  executed_volume: number | null;
  paid_fee: number | null;
  collected_at: number;
  trade_timestamp: string;
  created_at: string;
}

interface BtTradeCoinHistoryProps {
  title: string;
  startTime?: string;
  endTime?: string;
}

const CRYPTO_BASE = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8080'
  : '/mercury-backend';

const BtTradeCoinHistory: React.FC<BtTradeCoinHistoryProps> = ({ title, startTime, endTime }) => {
  const [realRecords, setRealRecords] = useState<TradeCoinRecord[]>([]);
  const [realLoading, setRealLoading] = useState(false);
  const [realError, setRealError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);

  const filterByTime = (data: TradeCoinRecord[]) => {
    if (!startTime || !endTime) return data;
    const startMs = Date.parse(startTime.endsWith('Z') ? startTime : startTime + 'Z');
    const endMs = Date.parse(endTime.endsWith('Z') ? endTime : endTime + 'Z');
    return data.filter(r => {
      const t = Date.parse(r.trade_timestamp.replace(' ', 'T') + 'Z');
      return t >= startMs && t <= endMs;
    });
  };

  const fetchRealRecords = useCallback(async () => {
    setRealLoading(true);
    setRealError(null);
    try {
      const res = await fetch(`${CRYPTO_BASE}/bt/upbit-orders/history?limit=500`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setRealRecords(filterByTime(json.data || []));
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Failed to fetch';
      if (raw === 'Failed to fetch') {
        setRealError('mercury-backend 서버(포트 8080)에 연결할 수 없습니다. 서버가 실행 중인지 확인하세요.');
      } else {
        setRealError(raw);
      }
    } finally {
      setRealLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime, endTime]);

  const handleImport = useCallback(async () => {
    setImporting(true);
    setImportResult(null);
    setRealError(null);
    try {
      const res = await fetch(`${CRYPTO_BASE}/bt/upbit-orders/import?limit=100`, { method: 'POST' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      setImportResult(`${json.imported}건 저장 (전체 ${json.fetched}건 조회)`);
      await fetchRealRecords();
    } catch (err) {
      setRealError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  }, [fetchRealRecords]);

  useEffect(() => {
    fetchRealRecords();
  }, [fetchRealRecords]);

  const calcPairProfit = (records: TradeCoinRecord[]) => {
    const byCode = new Map<string, TradeCoinRecord[]>();
    for (const r of records) {
      if (!byCode.has(r.code)) byCode.set(r.code, []);
      byCode.get(r.code)!.push(r);
    }
    let total = 0;
    let pairCount = 0;
    for (const list of Array.from(byCode.values())) {
      list.sort((a, b) => a.trade_timestamp.localeCompare(b.trade_timestamp));
      let pendingBuy: TradeCoinRecord | null = null;
      for (const r of list) {
        if (r.trade_type === 'BUY') {
          pendingBuy = r;
        } else if (r.trade_type === 'SELL' && pendingBuy) {
          const bp = pendingBuy.price ?? 0;
          const sp = r.price ?? 0;
          if (bp > 0 && sp > 0) {
            total += ((sp - bp) / bp) * 100;
            pairCount++;
          }
          pendingBuy = null;
        }
      }
    }
    return { total, pairCount };
  };

  const realProfit = React.useMemo(() => calcPairProfit(realRecords), [realRecords]);
  const formatTime = (t: string) => t ? t.replace('T', ' ').replace('Z', '').slice(0, 19) : '-';
  const fmtPrice = (v: number | null) => v != null ? v.toLocaleString() : '-';

  return (
    <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <h4 style={{ margin: 0, color: '#495057' }}>{title}</h4>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {importResult && (
            <span style={{ fontSize: '12px', color: '#198754' }}>{importResult}</span>
          )}
          <button
            onClick={handleImport}
            disabled={importing}
            style={{
              padding: '4px 12px',
              borderRadius: '4px',
              border: '1px solid #0d6efd',
              backgroundColor: importing ? '#ccc' : '#0d6efd',
              color: '#fff',
              fontSize: '12px',
              cursor: importing ? 'not-allowed' : 'pointer',
            }}
          >
            {importing ? '임포트중...' : 'Upbit 체결내역 임포트'}
          </button>
          <button
            onClick={fetchRealRecords}
            disabled={realLoading}
            style={{
              padding: '4px 12px',
              borderRadius: '4px',
              border: '1px solid #ced4da',
              backgroundColor: '#fff',
              fontSize: '12px',
              cursor: realLoading ? 'not-allowed' : 'pointer',
            }}
          >
            {realLoading ? '로딩중...' : '새로고침'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '15px' }}>
        {/* 왼쪽: Upbit 실제 체결내역 */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h5 style={{ margin: '0 0 8px 0', color: '#0d6efd', fontSize: '13px' }}>
            Upbit 실제 체결내역 (trade_coin)
            <span style={{ fontWeight: 'normal', color: '#6c757d', marginLeft: '8px' }}>
              ({realRecords.length}건)
            </span>
            {realProfit.pairCount > 0 && (
              <span style={{
                fontWeight: 'normal', marginLeft: '8px',
                color: realProfit.total > 0 ? '#dc3545' : realProfit.total < 0 ? '#0d6efd' : '#495057',
              }}>
                수익률 합 {realProfit.total > 0 ? '+' : ''}{realProfit.total.toFixed(2)}% ({realProfit.pairCount} 페어)
              </span>
            )}
          </h5>
          {realError && (
            <div style={{ padding: '8px', backgroundColor: '#fff5f5', color: '#dc3545', borderRadius: '4px', fontSize: '13px', marginBottom: '8px' }}>
              {realError}
            </div>
          )}
          {realRecords.length === 0 && !realLoading && !realError ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d', fontSize: '13px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              체결 데이터가 없습니다. "Upbit 체결내역 임포트" 버튼을 눌러주세요.
            </div>
          ) : (
            <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#d0e7ff', position: 'sticky', top: 0 }}>
                    <th style={thStyle}>매매</th>
                    <th style={thStyle}>코인</th>
                    <th style={thStyle}>가격</th>
                    <th style={thStyle}>수량</th>
                    <th style={thStyle}>체결량</th>
                    <th style={thStyle}>수수료</th>
                    <th style={thStyle}>시각</th>
                  </tr>
                </thead>
                <tbody>
                  {realRecords.map((r) => (
                    <tr key={r.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ ...tdStyle, color: r.trade_type === 'BUY' ? '#dc3545' : '#0d6efd', fontWeight: 600 }}>
                        {r.trade_type}
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{r.code}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtPrice(r.price)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{r.volume != null ? r.volume.toFixed(8) : '-'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{r.executed_volume != null ? r.executed_volume.toFixed(8) : '-'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{r.paid_fee != null ? r.paid_fee.toFixed(4) : '-'}</td>
                      <td style={{ ...tdStyle, fontSize: '11px' }}>{formatTime(r.trade_timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 오른쪽: 실제 매매기록 (구축예정) */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h5 style={{ margin: '0 0 8px 0', color: '#495057', fontSize: '13px' }}>
            실제 매매기록 (bt_actual_trade_coin)
            <span style={{ fontWeight: 'normal', color: '#6c757d', marginLeft: '8px' }}>
              구축예정
            </span>
          </h5>
          <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d', fontSize: '13px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
            bt_actual_trade_coin 테이블 구축 예정
          </div>
        </div>
      </div>
    </div>
  );
};

const thStyle: React.CSSProperties = {
  padding: '6px 8px',
  textAlign: 'left',
  fontWeight: 600,
  borderBottom: '2px solid #dee2e6',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '5px 8px',
  whiteSpace: 'nowrap',
  color: '#000',
};

export default BtTradeCoinHistory;
